import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { c as cl } from './clsx.m.js';
import { c as createTypeValidator, a as createLogger, e as emitEvent, i as isTriggerEvent, d as isNumber, y as updateStyles, g as getCSSValue, u as updateElementStyle, l as logDeprecatedEventOnce } from './utils.js';
import { d as defineCustomElement$5 } from './cl-chip2.js';
import { d as defineCustomElement$4 } from './cl-icon2.js';
import { d as defineCustomElement$3 } from './cl-scroll-wrapper2.js';
import { d as defineCustomElement$2 } from './cl-typography2.js';

// Constants
const component = 'cl-text-field';
const textFieldConstants = {
  AUTOCOMPLETE: {
    OFF: 'off',
    ON: 'on',
  },
  LABEL_ANIMATION: {
    TO_FIXED: 'to-fixed',
    TO_FLOATING: 'to-floating',
  },
  TYPE: {
    EMAIL: 'email',
    NUMBER: 'number',
    PASSWORD: 'password',
    SEARCH: 'search',
    TEXT: 'text',
    TEXTAREA: 'textarea',
  },
  VARIANT: {
    DEFAULT: 'default',
    SUBTLE: 'subtle',
  },
};
// Type-validators
const textFieldValidator = {
  autocomplete: createTypeValidator(textFieldConstants.AUTOCOMPLETE),
  labelAnimation: createTypeValidator(textFieldConstants.LABEL_ANIMATION),
  type: createTypeValidator(textFieldConstants.TYPE),
  variant: createTypeValidator(textFieldConstants.VARIANT),
};

const clTextFieldCss = ".sc-cl-text-field-h{--background-color:rgba(var(--color-neutral-50));--border-color:rgba(var(--color-neutral-200), var(--opacity-5));--content-height:calc(var(--spacing-1) * 5.5);--text-field-height:calc(var(--spacing-5) + var(--spacing-half));--text-field-width:calc(4 * var(--spacing-10))}.default.sc-cl-text-field-h{--fixed-label-offset-left:var(--spacing-2);--fixed-label-offset-top:calc((var(--spacing-2) + var(--spacing-half) + var(--spacing-quarter)) * -1);--spacing-left-side:var(--spacing-2);--spacing-right-side:var(--spacing-2)}.subtle.sc-cl-text-field-h{--fixed-label-offset-left:0;--fixed-label-offset-top:calc(var(--spacing-2) * -1);--spacing-left-side:0;--spacing-right-side:var(--spacing-1)}.sc-cl-text-field-h{display:flex;flex:1 0 auto;flex-wrap:wrap;font:var(--spacing-2) var(--font-family-ui);height:-moz-max-content;height:max-content;margin:var(--spacing-half) 0;max-width:var(--text-field-width);position:relative;width:100%}.sc-cl-text-field-h,.sc-cl-text-field-h *.sc-cl-text-field,.sc-cl-text-field-h *.sc-cl-text-field::before,.sc-cl-text-field-h *.sc-cl-text-field::after,.sc-cl-text-field-h .sc-cl-text-field-s>*,.sc-cl-text-field-h .sc-cl-text-field-s *,.sc-cl-text-field-h .sc-cl-text-field-s *::before,.sc-cl-text-field-h .sc-cl-text-field-s *::after{box-sizing:border-box}@keyframes to-fixed{0%{height:var(--content-height);left:var(--floating-label-offset-left);top:var(--floating-label-offset-top, 0)}50%{height:var(--spacing-2);top:var(--fixed-label-offset-top)}100%{height:var(--spacing-2);left:var(--fixed-label-offset-left);top:var(--fixed-label-offset-top)}}@keyframes to-floating{0%{left:var(--fixed-label-offset-left);top:var(--fixed-label-offset-top)}50%{top:var(--fixed-label-offset-top)}100%{left:var(--floating-label-offset-left);top:var(--floating-label-offset-top, 0)}}.sc-cl-text-field-h cl-scroll-wrapper.sc-cl-text-field{--wrapper-padding-inline:0;width:100%}.sc-cl-text-field-h button.sc-cl-text-field:focus,.sc-cl-text-field-h input.sc-cl-text-field:focus,.sc-cl-text-field-h select.sc-cl-text-field:focus,.sc-cl-text-field-h textarea.sc-cl-text-field:focus{outline:none}.sc-cl-text-field-h .text-field__label-wrapper.sc-cl-text-field{flex:1 0 100%;height:var(--spacing-2)}.sc-cl-text-field-h .text-field__label.sc-cl-text-field{align-items:center;color:var(--text-field-color-label, rgba(var(--color-neutral-800)));display:flex;position:absolute;-webkit-user-select:none;-moz-user-select:none;user-select:none}.sc-cl-text-field-h .text-field__label__fixed.sc-cl-text-field{height:var(--spacing-2);left:var(--fixed-label-offset-left);top:var(--fixed-label-offset-top);transition:all 150ms}.sc-cl-text-field-h .text-field__label__floating.sc-cl-text-field{height:var(--content-height);left:var(--floating-label-offset-left);padding:0 var(--spacing-quarter);top:var(--floating-label-offset-top, 0);transition:all 150ms}.sc-cl-text-field-h .text-field__label--to-fixed.sc-cl-text-field{animation:to-fixed 150ms forwards cubic-bezier(0, 0, 0.58, 1)}.sc-cl-text-field-h .text-field__label--to-floating.sc-cl-text-field{animation:to-floating 150ms forwards cubic-bezier(0, 0, 0.58, 1)}.sc-cl-text-field-h .text-field__input.sc-cl-text-field{-webkit-appearance:none;background-color:transparent;border:none;caret-color:rgba(var(--color-brand-700));color:rgb(var(--font-color-onlight-default));display:flex;flex:1 1 auto;font-family:var(--font-family-ui);font-size:var(--font-size-body-1);font-weight:var(--font-weight-400);height:var(--content-height);outline:none;outline-width:0;overflow:hidden;resize:none;width:100%;z-index:var(--z-index-1)}.sc-cl-text-field-h .text-field__input.sc-cl-text-field:-webkit-autofill,.sc-cl-text-field-h .text-field__input.sc-cl-text-field:-webkit-autofill:hover,.sc-cl-text-field-h .text-field__input.sc-cl-text-field:-webkit-autofill:focus{border:none;-webkit-box-shadow:0 0 0 1000px rgba(var(--color-brand-50), var(--opacity-5)) inset !important;-webkit-text-fill-color:rgb(var(--color-neutral-900)) !important}.sc-cl-text-field-h .text-field__input-wrapper.sc-cl-text-field{background-color:transparent;display:flex;flex:1 1 100%;height:-moz-max-content;height:max-content;max-width:100%;min-height:var(--text-field-height, calc(var(--spacing-5) + var(--spacing-half)));padding:0 var(--spacing-right-side) 0 var(--spacing-left-side);position:relative}.sc-cl-text-field-h .text-field__input-wrapper.sc-cl-text-field:hover{--border-color:rgba(var(--color-neutral-200), var(--opacity-3))}.sc-cl-text-field-h .text-field__input-wrapper.sc-cl-text-field::before{background-color:var(--background-color);content:\"\";position:absolute}.sc-cl-text-field-h .text-field__input-wrapper__border.sc-cl-text-field{overflow:hidden;position:absolute}.sc-cl-text-field-h .text-field__input-wrapper__content.sc-cl-text-field{display:flex;flex:auto;height:-moz-max-content;height:max-content;min-height:var(--text-field-height);z-index:var(--z-index-1)}.sc-cl-text-field-h .text-field__input-wrapper__content.sc-cl-text-field textarea.sc-cl-text-field{min-height:var(--text-field-height);width:100%}.sc-cl-text-field-h .text-field__input-wrapper__content.sc-cl-text-field textarea.sc-cl-text-field:-moz-placeholder-shown{color:rgba(var(--font-color-onlight-muted));font-family:var(--font-family-ui);font-size:var(--font-size-body-1);font-weight:var(--font-weight-400);line-height:var(--content-height);padding:0 var(--spacing-quarter)}.sc-cl-text-field-h .text-field__input-wrapper__content.sc-cl-text-field textarea.sc-cl-text-field:placeholder-shown{color:rgba(var(--font-color-onlight-muted));font-family:var(--font-family-ui);font-size:var(--font-size-body-1);font-weight:var(--font-weight-400);line-height:var(--content-height);padding:0 var(--spacing-quarter)}.sc-cl-text-field-h .text-field__input-wrapper__content.sc-cl-text-field input.sc-cl-text-field{line-height:var(--content-height)}.sc-cl-text-field-h .text-field__assistive-content.sc-cl-text-field{color:rgb(var(--font-color-onlight-default));display:flex;flex:1 1 100%;font-family:var(--font-family-ui);font-size:var(--font-size-microcopy);height:var(--spacing-2);margin-top:var(--spacing-half);padding:0 var(--spacing-left-side);-webkit-user-select:none;-moz-user-select:none;user-select:none}.sc-cl-text-field-h .text-field__assistive-content__text.sc-cl-text-field{color:var(--text-field-color-assistive-text, rgba(var(--color-neutral-800)));display:flex;flex:1 0 70%;justify-content:flex-start}.sc-cl-text-field-h .text-field__assistive-content__counter.sc-cl-text-field{color:var(--text-field-color-counter, rgba(var(--color-neutral-800)));display:flex;flex:1 0 auto;justify-content:flex-end}.sc-cl-text-field-h .text-field__input-wrapper__slot.sc-cl-text-field{align-items:center;display:flex;height:var(--text-field-height);justify-content:center;max-height:var(--content-height);position:relative;z-index:var(--z-index-1)}.sc-cl-text-field-h .sc-cl-text-field-s>[slot=leading]{color:rgba(var(--color-brand-600));max-height:var(--spacing-3);max-width:var(--spacing-4);padding-right:var(--spacing-half)}.sc-cl-text-field-h .sc-cl-text-field-s>[slot=leading]:last-child{padding-right:var(--spacing-1)}.sc-cl-text-field-h .sc-cl-text-field-s>cl-icon[slot=action]{--icon-color:rgba(var(--color-neutral-900));color:rgba(var(--color-neutral-900))}.sc-cl-text-field-h .sc-cl-text-field-s>cl-icon[slot=action]:first-child{padding-left:var(--spacing-1)}.sc-cl-text-field-h .sc-cl-text-field-s>cl-icon-button[slot=action]{margin-right:calc(-1 * var(--spacing-1))}.sc-cl-text-field-h .text-field__input-wrapper__content--has-tags.sc-cl-text-field{flex-wrap:wrap}.sc-cl-text-field-h .text-field__input-wrapper__content--has-tags.sc-cl-text-field input.sc-cl-text-field{flex:1 0 auto;order:2}.sc-cl-text-field-h .text-field__input-wrapper__content--tags-wrapper.sc-cl-text-field{display:flex;flex:1 0 100%;flex-wrap:wrap;margin-bottom:calc(var(--spacing-half) * -1);order:1;padding-block-start:var(--spacing-half);width:var(--input-element-width, auto)}.sc-cl-text-field-h .text-field__input-wrapper__content--tags-wrapper.sc-cl-text-field cl-chip.sc-cl-text-field{margin:var(--spacing-quarter) var(--spacing-half)}.default.sc-cl-text-field-h .text-field__label-wrapper.sc-cl-text-field{margin-bottom:calc(var(--spacing-half) + var(--spacing-quarter))}.default.sc-cl-text-field-h .text-field__input-wrapper.sc-cl-text-field{border-color:var(--border-width) solid transparent;border-radius:var(--border-radius-interactive-small)}.default.sc-cl-text-field-h .text-field__input-wrapper.sc-cl-text-field::before{background-clip:padding-box;border:var(--border-width) solid transparent;border-radius:var(--border-radius-interactive-small);box-sizing:border-box;inset:0;z-index:var(--z-index-1)}.default.sc-cl-text-field-h .text-field__input-wrapper__border.sc-cl-text-field{align-items:center;border-radius:var(--border-radius-interactive-small);display:flex;inset:0;justify-content:center;-webkit-mask-image:-webkit-radial-gradient(white, black);mask-image:-webkit-radial-gradient(white, black);z-index:0}.default.sc-cl-text-field-h .text-field__input-wrapper__border.sc-cl-text-field::after{background-color:var(--border-color);border-radius:var(--border-radius-interactive-small);content:\"\";height:100%;position:absolute;width:100%;z-index:calc(var(--z-index-2) * -1)}.subtle.sc-cl-text-field-h .text-field__label-wrapper.sc-cl-text-field{margin-bottom:0}.subtle.sc-cl-text-field-h .text-field__input-wrapper.sc-cl-text-field::before{height:100%;left:0;top:0;width:100%;z-index:var(--z-index-1)}.subtle.sc-cl-text-field-h .text-field__input-wrapper__border.sc-cl-text-field{background-color:var(--border-color);bottom:calc(var(--spacing-quarter) * -1);height:var(--border-width);width:100%}.sc-cl-text-field-h .text-field__input.sc-cl-text-field:-webkit-autofill{animation-name:on-browser-autofill-start}.sc-cl-text-field-h .text-field__input.sc-cl-text-field:not(:-webkit-autofill){animation-name:on-browser-autofill-cancel}@keyframes on-browser-autofill-start{from{}to{}}@keyframes on-browser-autofill-cancel{from{}to{}}.sc-cl-text-field-h .text-field__input-wrapper__content__grow-wrap.sc-cl-text-field{display:grid}.sc-cl-text-field-h .text-field__input-wrapper__content__grow-wrap.sc-cl-text-field::after{content:attr(data-replicated-value) \" \";visibility:hidden;white-space:pre-wrap}.sc-cl-text-field-h .text-field__input-wrapper__content__grow-wrap.sc-cl-text-field textarea.sc-cl-text-field{overflow:hidden;resize:none}.sc-cl-text-field-h .text-field__input-wrapper__content__grow-wrap.sc-cl-text-field textarea.sc-cl-text-field,.sc-cl-text-field-h .text-field__input-wrapper__content__grow-wrap.sc-cl-text-field::after{grid-area:1/1/2/2;padding-block-start:calc(var(--spacing-1) + var(--spacing-half));word-break:break-word}.sc-cl-text-field-h .text-field__input-wrapper__content__grow-wrap--autoresize.sc-cl-text-field::after{margin-block-end:var(--spacing-2)}.sc-cl-text-field-h .text-field__input-wrapper__content__grow-wrap--autoresize.sc-cl-text-field textarea.sc-cl-text-field{height:auto;margin-block-end:var(--spacing-2)}.textarea.show-textarea.sc-cl-text-field-h{--text-field-height:calc(var(--content-height) * 2.5)}.textarea.show-textarea.sc-cl-text-field-h cl-scroll-wrapper.sc-cl-text-field{--wrapper-max-height:var(--text-field-height)}.search.sc-cl-text-field-h{--border-color:rgba(var(--color-neutral-200))}.search.sc-cl-text-field-h .text-field__input.sc-cl-text-field:hover{cursor:pointer}.search.sc-cl-text-field-h .text-field__input-wrapper.sc-cl-text-field:hover{--border-color:rgba(var(--color-neutral-200), var(--opacity-3))}.search.sc-cl-text-field-h .text-field__input.sc-cl-text-field::-webkit-search-decoration,.search.sc-cl-text-field-h .text-field__input.sc-cl-text-field::-webkit-search-cancel-button,.search.sc-cl-text-field-h .text-field__input.sc-cl-text-field::-webkit-search-results-button,.search.sc-cl-text-field-h .text-field__input.sc-cl-text-field::-webkit-search-results-decoration{-webkit-appearance:none}.number.sc-cl-text-field-h .text-field__input.sc-cl-text-field{-moz-appearance:textfield}.number.sc-cl-text-field-h .text-field__input.sc-cl-text-field::-webkit-inner-spin-button,.number.sc-cl-text-field-h .text-field__input.sc-cl-text-field::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}.number.sc-cl-text-field-h .text-field__input__arrows.sc-cl-text-field{cursor:pointer;display:flex;flex-direction:column;height:var(--content-height);justify-content:center;order:3}.number.sc-cl-text-field-h .text-field__input__arrows.sc-cl-text-field>cl-icon.sc-cl-text-field{display:flex;flex:1 0 auto;width:var(--spacing-3)}.number.sc-cl-text-field-h .text-field__input__arrows__up.sc-cl-text-field{padding-top:calc(var(--spacing-half) + var(--spacing-eighth))}.number.sc-cl-text-field-h .text-field__input__arrows__down.sc-cl-text-field{padding-bottom:calc(var(--spacing-half) + var(--spacing-eighth))}.number.sc-cl-text-field-h .text-field__input__arrows--limit.sc-cl-text-field{color:rgba(var(--color-neutral-500));cursor:default}.processing.sc-cl-text-field-h{--border-color:rgba(var(--color-brand-200))}@keyframes spinning-progress{100%{transform:rotate(1turn)}}@keyframes increasing-progress{from{left:-15%;width:15%}to{left:105%;width:80%}}@keyframes decreasing-progress{from{left:-80%;width:80%}to{left:110%;width:10%}}.processing.default.sc-cl-text-field-h .text-field__input-wrapper.sc-cl-text-field:hover{--border-color:rgba(var(--color-brand-200))}.processing.default.sc-cl-text-field-h .text-field__input-wrapper__border.sc-cl-text-field::before{animation:spinning-progress 2s infinite linear;background-color:rgba(var(--color-brand-main));border-radius:var(--border-radius-interactive-small);content:\"\";height:calc(var(--current-element-width, auto) * 1.1);position:absolute;top:50%;transform-origin:top center;width:100%;z-index:calc(var(--z-index-1) * -1)}.processing.subtle.sc-cl-text-field-h .text-field__input-wrapper__border.sc-cl-text-field::before{animation:increasing-progress 2s infinite;background-color:rgba(var(--color-brand-main));content:\"\";height:100%;position:absolute}.processing.subtle.sc-cl-text-field-h .text-field__input-wrapper__border.sc-cl-text-field::after{animation:decreasing-progress 2s 1s infinite;background-color:rgba(var(--color-brand-main));content:\"\";height:100%;position:absolute}.disabled.sc-cl-text-field-h{--border-color:rgba(var(--color-neutral-100))}.sc-cl-text-field-h.disabled .sc-cl-text-field-s>[slot=leading]{filter:grayscale(1) contrast(0.5) opacity(0.29)}.sc-cl-text-field-h.disabled .sc-cl-text-field-s>[slot=action]{filter:grayscale(1) contrast(0) opacity(0.88)}.disabled.default.sc-cl-text-field-h{--background-color:rgba(var(--color-neutral-900), var(--opacity-1))}.disabled.sc-cl-text-field-h .text-field__label.sc-cl-text-field{color:rgba(var(--font-color-onlight-muted));cursor:not-allowed}.disabled.sc-cl-text-field-h .text-field__input.sc-cl-text-field{color:rgba(var(--font-color-onlight-muted));-webkit-user-select:none;-moz-user-select:none;user-select:none}.disabled.sc-cl-text-field-h .text-field__input.sc-cl-text-field::-moz-selection{-moz-user-select:none;user-select:none}.disabled.sc-cl-text-field-h .text-field__input.sc-cl-text-field::selection{-webkit-user-select:none;-moz-user-select:none;user-select:none}.disabled.sc-cl-text-field-h .text-field__input__arrows.sc-cl-text-field{cursor:not-allowed}.disabled.sc-cl-text-field-h .text-field__input__arrows.sc-cl-text-field>cl-icon.sc-cl-text-field{color:rgba(var(--color-neutral-500));cursor:not-allowed}.disabled.sc-cl-text-field-h .text-field__input-wrapper.sc-cl-text-field:hover{--border-color:rgba(var(--color-neutral-100));cursor:not-allowed}.disabled.sc-cl-text-field-h .text-field__input-wrapper.sc-cl-text-field:hover *.sc-cl-text-field{cursor:not-allowed}.disabled.sc-cl-text-field-h .text-field__input-wrapper__content--tags-wrapper.sc-cl-text-field:hover{cursor:not-allowed}.disabled.sc-cl-text-field-h .text-field__input-wrapper__content--tags-wrapper.sc-cl-text-field:hover cl-chip.sc-cl-text-field{cursor:not-allowed}.disabled.sc-cl-text-field-h .text-field__assistive-content.sc-cl-text-field *.sc-cl-text-field{color:rgba(var(--font-color-onlight-muted));cursor:not-allowed}.full-width.sc-cl-text-field-h{--text-field-width:100%}.focused.sc-cl-text-field-h .text-field__label.sc-cl-text-field{color:rgba(var(--color-neutral-800))}.focused.sc-cl-text-field-h .text-field__input-wrapper.sc-cl-text-field{--border-color:rgba(var(--color-brand-600))}.focused.sc-cl-text-field-h .text-field__input-wrapper.sc-cl-text-field:hover{--border-color:rgba(var(--color-brand-main))}.error.sc-cl-text-field-h:not(.search) .text-field__input-wrapper.sc-cl-text-field{--border-color:rgba(var(--system-color-error-main))}.error.sc-cl-text-field-h:not(.search) .text-field__input-wrapper.sc-cl-text-field:hover{--border-color:rgba(var(--system-color-error-main))}.error.sc-cl-text-field-h:not(.search) .text-field__assistive-content.sc-cl-text-field *.sc-cl-text-field{color:rgba(var(--system-color-error-main))}.sc-cl-text-field-h.read-only:not(.search) .sc-cl-text-field-s>[slot=action]{filter:grayscale(1) contrast(0) opacity(0.88)}.read-only.sc-cl-text-field-h:not(.search) .text-field__label.sc-cl-text-field{color:rgba(var(--color-neutral-800))}.read-only.sc-cl-text-field-h:not(.search) .text-field__input.sc-cl-text-field{color:rgba(var(--color-neutral-800));opacity:1;-webkit-text-fill-color:rgba(var(--color-neutral-800))}.read-only.sc-cl-text-field-h:not(.search) .text-field__input.sc-cl-text-field:-moz-placeholder-shown{color:rgba(var(--font-color-onlight-muted));-webkit-text-fill-color:rgba(var(--font-color-onlight-muted))}.read-only.sc-cl-text-field-h:not(.search) .text-field__input.sc-cl-text-field:placeholder-shown{color:rgba(var(--font-color-onlight-muted));-webkit-text-fill-color:rgba(var(--font-color-onlight-muted))}.read-only.sc-cl-text-field-h:not(.search) .text-field__input-wrapper.sc-cl-text-field{background-color:transparent}.read-only.sc-cl-text-field-h:not(.search) .text-field__assistive-content__text.sc-cl-text-field{flex:1 0 100%}.read-only.sc-cl-text-field-h:not(.search) .text-field__assistive-content__counter.sc-cl-text-field{visibility:hidden}.read-only.sc-cl-text-field-h:not(.search).number .text-field__input__arrows.sc-cl-text-field{visibility:hidden}.read-only.sc-cl-text-field-h:not(.search):not(.processing) .text-field__input-wrapper.sc-cl-text-field{--border-color:transparent}.read-only.sc-cl-text-field-h:not(.search):not(.processing) .text-field__input-wrapper.sc-cl-text-field:hover{--border-color:transparent}";

const ClTextField$1 = /*@__PURE__*/ proxyCustomElement(class ClTextField extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.clInput = createEvent(this, "clInput", 7);
    this.input = createEvent(this, "input", 7);
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    /**
     * ResizeObserver object
     */
    this.resizeObserver = new ResizeObserver(() => this.handleResizeTextArea());
    /**
     * Timeout for the increment/decrement-number functions
     */
    this.timeout = undefined;
    /**
     * Interval for the increment/decrement-number functions
     */
    this.interval = undefined;
    /**
     * Whether the floating label should be shown or not
     */
    this.showFloatingLabel = false;
    /**
     * Handler for the clearInput method
     */
    this.bindClearInput = this.clearInput.bind(this);
    /**
     * Handler for the removeTag method
     */
    this.bindRemoveTag = this.removeTag.bind(this);
    /**
     * Handles updating the CSS classes object
     */
    this.updateClasses = () => {
      const updatedClasses = {
        disabled: this.disabled,
        error: this.error && !this.disabled,
        focused: this.isFocused && !this.error && !this.disabled && !this.processing && !this.readOnly,
        'full-width': this.fullWidth,
        processing: this.processing && !this.error && !this.disabled,
        'read-only': this.readOnly && !this.error && !this.disabled && this.type !== 'search',
        'show-textarea': this.showTextArea,
      };
      if (JSON.stringify(this.classes) !== JSON.stringify(updatedClasses)) {
        this.classes = updatedClasses;
      }
    };
    /**
     * Handles validating if the curent floating-label and placeholder combination is valid
     */
    this.validateFloatingLabelPlaceholder = (floatingLabel, placeholder) => {
      if (placeholder && floatingLabel && placeholder.length) {
        this.logger.error.attribute.floatingLabel.placeholder("It's not alowed to use both placeholder and floatingLabel at the same time.");
      }
    };
    /**
     * Handles updating the label-classes when the animation starts, so it moves to fixed or floating position correctly
     */
    this.handleLabelAnimationStart = (event) => {
      if (textFieldValidator.labelAnimation(event.animationName)) {
        Object.values(textFieldConstants.LABEL_ANIMATION)
          .filter(animation => animation !== event.animationName)
          .forEach(animation => { var _a; return (_a = this.labelElement) === null || _a === void 0 ? void 0 : _a.classList.remove(`text-field__label--${animation}`); });
      }
    };
    /**
     * Handles updating the label-classes when the animation ends, so it moves to fixed or floating position correctly
     */
    this.handleLabelAnimationEnd = (event) => {
      if (textFieldValidator.labelAnimation(event.animationName)) {
        Object.values(textFieldConstants.LABEL_ANIMATION).forEach(animation => { var _a; return (_a = this.labelElement) === null || _a === void 0 ? void 0 : _a.classList.remove(`text-field__label--${animation}`); });
        this.isAnimating = false;
      }
    };
    /**
     * Function that checks if the input is being autofilled by the browser
     */
    this.handleAutoFilledByBrowserAnimation = (event) => {
      if (event.animationName === 'on-browser-autofill-start') {
        this.isAutoFilledByBrowser = true;
      }
      else if (event.animationName === 'on-browser-autofill-cancel') {
        this.isAutoFilledByBrowser = false;
      }
    };
    /**
     * Handles emitting input value changes WHILE the input field is focused
     */
    this.handleOnInput = (event) => {
      event.preventDefault();
      event.stopPropagation();
      const newValue = event.target ? event.target.value : '';
      const emitInputEvent = () => emitEvent(this, 'clInput', { component, originalEvent: event, value: newValue }, newValue);
      if (this.inputElement) {
        if (this.uncontrolled) {
          this.value = event.target.value;
          emitInputEvent();
        }
        else {
          emitInputEvent();
          this.inputElement.value = this.value;
        }
      }
    };
    /**
     * Handles picking an dropdown-option through the Enter key
     */
    this.handleOnKeyPress = (event) => {
      var _a, _b, _c;
      if (isTriggerEvent(event)) {
        if (this.allowSubmit === false) {
          event.preventDefault();
          event.stopPropagation();
        }
        const newValue = ((_a = this.tags) === null || _a === void 0 ? void 0 : _a.length) ? { tags: this.tags, enter: true } : (_c = (_b = this.inputElement) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : '';
        if (this.inputElement) {
          emitEvent(this, 'clInput', { component, originalEvent: event, value: newValue }, newValue);
        }
      }
    };
    /**
     * Handles enabling the focus state
     *
     */
    this.handleOnFocus = (event) => {
      if (!this.disabled) {
        this.isFocused = true;
        if (this.autoSelect) {
          // Makes the select function async so the browser calls it after the focus event.
          setTimeout(() => {
            event.target.select();
          }, 0);
        }
        if (this.labelElement && this.floatingLabel && this.showFloatingLabel) {
          this.isAnimating = true;
          this.labelElement.classList.add('text-field__label--to-fixed');
        }
      }
    };
    /**
     * Handles enabling the blur state
     *
     */
    this.handleOnBlur = () => {
      if (!this.disabled) {
        this.isFocused = false;
        if (this.labelElement && this.floatingLabel && !this.showFloatingLabel && !`${this.value}`.length) {
          this.labelElement.classList.add('text-field__label--to-floating');
        }
      }
    };
    /**
     * Handles parsing a given string to a given number of decimals
     */
    this.parseFloating = (value, decimals) => {
      let string = `${value}`;
      const index = string.indexOf('.');
      string += index > 0 && string.slice(index + 1).length === 1 ? '0' : '.00';
      return string.slice(0, string.indexOf('.') + decimals + 1);
    };
    /**
     * Handles incrementing the value in the number-type text-field by the current amount of steps
     */
    this.handleIncrementNumber = (event) => {
      if (!(isNumber(this.max) && parseFloat(this.value) >= this.max) && !this.disabled && !this.readOnly) {
        const isFloat = this.value.indexOf('.') !== -1;
        let newValue;
        if (isFloat) {
          newValue = this.parseFloating(+this.value + this.step, 2);
        }
        else {
          if (this.inputElement) {
            if (this.value.length === 0 || !isNumber(+this.value)) {
              this.inputElement.value = `${this.min}`;
            }
            if (isFloat) {
              newValue = this.parseFloating(+this.value + this.step, 2);
            }
            else {
              if (this.value.length === 0 || !isNumber(+this.value)) {
                this.inputElement.value = `${this.min}`;
              }
              this.inputElement.stepUp();
              newValue = this.inputElement.value;
            }
            emitEvent(this, 'clInput', { component, originalEvent: event, value: newValue }, newValue);
            if (this.uncontrolled) {
              this.value = newValue;
            }
            else {
              this.inputElement.value = this.value;
            }
          }
        }
      }
    };
    /**
     * Handles decrementing the value in the number-type text-field by the current amount of steps
     */
    this.handleDecrementNumber = (event) => {
      if (!(isNumber(this.min) && parseFloat(this.value) <= this.min) && !this.disabled && !this.readOnly) {
        const isFloat = this.value.indexOf('.') !== -1;
        let newValue;
        if (this.inputElement) {
          if (isFloat) {
            newValue = this.parseFloating(+this.value - this.step, 2);
          }
          else {
            if (this.value.length === 0 || !isNumber(+this.value)) {
              this.inputElement.value = `${this.min}`;
            }
            if (isFloat) {
              newValue = this.parseFloating(+this.value - this.step, 2);
            }
            else {
              if (this.value.length === 0 || !isNumber(+this.value)) {
                this.inputElement.value = `${this.min}`;
              }
              this.inputElement.stepDown();
              newValue = this.inputElement.value;
            }
            emitEvent(this, 'clInput', { component, originalEvent: event, value: newValue }, newValue);
            if (this.uncontrolled) {
              this.value = newValue;
            }
            else {
              this.inputElement.value = this.value;
            }
          }
        }
      }
    };
    /**
     * Calculates the position that the floating label should have
     */
    this.calculateLabelPosition = () => {
      if ((this.showFloatingLabel && this.inputElement) ||
        (this.showFloatingLabel && this.inputElement && this.scrollWrapperElement)) {
        const cssVarOffsetLeft = '--floating-label-offset-left';
        const cssVarOffsetTop = '--floating-label-offset-top';
        const inputElement = this.scrollWrapperElement || this.inputElement;
        const offsetLeft = `${inputElement.offsetLeft}px`;
        const offsetTop = `${this.inputElement.offsetTop}px`;
        let tempStyles = this.styles;
        if (offsetLeft !== tempStyles[cssVarOffsetLeft]) {
          tempStyles = updateStyles(tempStyles, cssVarOffsetLeft, offsetLeft);
        }
        if (this.tags.length && offsetTop !== tempStyles[cssVarOffsetTop]) {
          tempStyles = updateStyles(tempStyles, cssVarOffsetTop, offsetTop);
        }
        else if (!this.tags.length && tempStyles[cssVarOffsetTop]) {
          tempStyles = updateStyles(tempStyles, cssVarOffsetTop);
        }
        this.styles = tempStyles;
      }
    };
    /**
     * Updates the text-field styles based on the current element width
     */
    this.getElementWidth = () => {
      if (this.element) {
        const cssVariable = '--current-element-width';
        const width = `${this.element.offsetWidth}px`;
        if (width !== getCSSValue(cssVariable, this.element)) {
          updateElementStyle(this.element, width, cssVariable);
        }
      }
    };
    /**
     * Handles updating the textArea height when having properties multiline or textarea-type
     */
    this.handleResizeTextArea = () => {
      var _a;
      if (this.showTextArea && this.inputElement && this.growWrapElement) {
        const { scrollHeight } = this.inputElement;
        const minHeight = +getCSSValue('min-height', this.inputElement).replace('px', '');
        if (!isNumber(minHeight))
          return;
        const classList = Array.from((_a = this.growWrapElement) === null || _a === void 0 ? void 0 : _a.classList);
        const className = 'text-field__input-wrapper__content__grow-wrap--autoresize';
        if (this.value.length > 0) {
          if (scrollHeight - 3 > minHeight) {
            if (!classList.includes(className)) {
              this.growWrapElement.classList.add(className);
            }
          }
          else if (classList.includes(className)) {
            this.growWrapElement.classList.remove(className);
          }
        }
        else if (classList.includes(className)) {
          this.growWrapElement.classList.remove(className);
        }
      }
    };
    /**
     * Handles clearing the throttle timer and interval when they are set
     */
    this.clearTimers = () => {
      clearTimeout(this.timeout);
      clearInterval(this.interval);
    };
    /**
     * Handles throttling the given function
     */
    this.throttleFunction = (fnToThrottle) => () => {
      if (!this.disabled && !this.readOnly) {
        this.timeout = setTimeout(() => {
          this.interval = setInterval(() => {
            fnToThrottle();
          }, 60);
        }, 300);
      }
    };
    /**
     * Renders all tags in the tags-array property
     */
    this.renderTags = () => this.tags.map(({ label, value }, index) => (h("cl-chip", { key: `${label}-${value}`, class: 'tag-chip', disabled: this.disabled, "icon-name": 'action--close', "icon-position": 'right', id: `${label}--${index}`, readOnly: this.readOnly, selected: true, small: true, onClIconClick: this.bindRemoveTag }, h("cl-typography", null, label))));
    /**
     * Renders a textarea or input-element based this.showTextArea
     */
    this.renderInputElement = () => {
      var _a, _b, _c, _d;
      const { autocomplete, max, min, step, type } = this;
      return this.showTextArea ? (h("cl-scroll-wrapper", { ref: el => {
          this.scrollWrapperElement = el;
        }, thumbOnly: true }, h("div", { ref: el => {
          this.growWrapElement = el;
        }, class: 'text-field__input-wrapper__content__grow-wrap' }, h("textarea", Object.assign({ ref: el => {
          this.inputElement = el;
        }, class: 'text-field__input', disabled: this.disabled || this.classes['read-only'], maxlength: this.maxLength, placeholder: this.placeholder, required: this.required, rows: 1, value: this.value }, (((_a = this.inputName) === null || _a === void 0 ? void 0 : _a.length) ? { name: this.inputName } : {}), (((_b = this.inputId) === null || _b === void 0 ? void 0 : _b.length) ? { id: this.inputId } : {}), this.inputElementAttributes))))) : (h("input", Object.assign({ ref: el => {
          this.inputElement = el;
        }, autocomplete: autocomplete, class: 'text-field__input', disabled: this.disabled || this.classes['read-only'], maxlength: this.maxLength, placeholder: this.placeholder, required: this.required, type: type, value: this.value }, (((_c = this.inputName) === null || _c === void 0 ? void 0 : _c.length) ? { name: this.inputName } : {}), (((_d = this.inputId) === null || _d === void 0 ? void 0 : _d.length) ? { id: this.inputId } : {}), (type === 'number' && Object.assign(Object.assign(Object.assign({}, (isNumber(min) ? { min } : {})), (isNumber(max) ? { max } : { max: Infinity })), (isNumber(step) ? { step } : { step: 1 }))), this.inputElementAttributes)));
    };
    this.growWrapElement = undefined;
    this.inputElement = undefined;
    this.scrollWrapperElement = undefined;
    this.styles = {};
    this.classes = {};
    this.inputElementAttributes = {};
    this.hasTagElements = false;
    this.isFocused = false;
    this.isAnimating = false;
    this.isAutoFilledByBrowser = false;
    this.showTextArea = false;
    this.allowSubmit = false;
    this.autocomplete = 'on';
    this.autoSelect = false;
    this.counter = false;
    this.disabled = false;
    this.error = false;
    this.floatingLabel = false;
    this.fullWidth = false;
    this.helperText = '';
    this.inputId = '';
    this.label = '';
    this.max = Infinity;
    this.maxLength = undefined;
    this.min = undefined;
    this.multiline = false;
    this.multiple = false;
    this.inputName = '';
    this.placeholder = '';
    this.processing = false;
    this.readOnly = false;
    this.required = false;
    this.step = 1;
    this.tags = [];
    this.type = 'text';
    this.uncontrolled = false;
    this.value = '';
    this.variant = 'default';
  }
  /**
   * Validates if the floating-label-attribute is not enabled while showing a placeholder
   */
  validateFloatingLabel(floatingLabel) {
    this.validateFloatingLabelPlaceholder(floatingLabel, this.placeholder);
  }
  /**
   * Validates if the floating-label-attribute is not enabled while showing a placeholder
   */
  validatePlaceholder(placeholder) {
    this.validateFloatingLabelPlaceholder(this.floatingLabel, placeholder);
  }
  /**
   * Validates if label-attribute shows an asterisk when the text-field is required
   */
  handleRequired(required) {
    if (this.label) {
      if (required && !this.label.endsWith('*')) {
        this.label = `${this.label}*`;
      }
      if (!required && this.label.endsWith('*')) {
        this.label = this.label.replace('*', '');
      }
    }
  }
  /**
   * Updates the hasTagElements state when the tags-count changes
   */
  validateTags(tags) {
    this.hasTagElements = !!tags.length;
  }
  /**
   * Validates if the given type-attribute is valid
   */
  validateType(type) {
    if (!textFieldValidator.type(type)) {
      this.logger.error.attribute.type(`Given invalid value "${type}".`);
    }
  }
  /**
   * Validates if the given variant-attribute is valid
   */
  validateVariant(variant) {
    if (!textFieldValidator.variant(variant)) {
      this.logger.error.attribute.variant(`Given invalid value "${variant}".`);
    }
  }
  /**
   * Updates the growWrap element content with the content of the textArea element, so it will resize correctly
   */
  updateGrowWrapElementContent() {
    this.showTextArea = !!((this.type === 'textarea' || (this.type === 'text' && this.multiline)) &&
      !this.hasTagElements);
    if (this.inputElement && this.showTextArea) {
      const parentNode = this.inputElement.parentNode;
      parentNode.dataset.replicatedValue = this.value;
    }
  }
  /**
   * Handles observing/unobserving the resizing of the growWrapElement
   */
  observeGrowWrapElementResize() {
    if (this.growWrapElement) {
      if (this.showTextArea) {
        this.resizeObserver.observe(this.growWrapElement);
      }
      else {
        this.resizeObserver.unobserve(this.growWrapElement);
      }
    }
  }
  /**
   * Every time the component is connected to the DOM:
   * - Console log deprecation warnings;
   * - Validate properties;
   */
  connectedCallback() {
    logDeprecatedEventOnce(this.logger, 'input', 'clInput');
    this.handleRequired(this.required);
    this.validateType(this.type);
    this.validateVariant(this.variant);
    this.validateFloatingLabelPlaceholder(this.floatingLabel, this.placeholder);
  }
  /**
   * Handles removing eventlisteners when unmounting the component from the DOM
   */
  disconnectedCallback() {
    var _a, _b, _c;
    if (this.type === 'number') {
      (_a = this.arrowUpElement) === null || _a === void 0 ? void 0 : _a.removeEventListener('mousedown', this.throttleFunction(this.handleIncrementNumber));
      (_b = this.arrowDownElement) === null || _b === void 0 ? void 0 : _b.removeEventListener('mousedown', this.throttleFunction(this.handleDecrementNumber));
    }
    this.element.removeEventListener('animationstart', this.handleLabelAnimationStart);
    this.element.removeEventListener('animationend', this.handleLabelAnimationEnd);
    (_c = this.inputElement) === null || _c === void 0 ? void 0 : _c.removeEventListener('animationstart', this.handleAutoFilledByBrowserAnimation);
    this.resizeObserver.disconnect();
  }
  /**
   * Handles adding eventlisteners when mounting the component from the DOM
   */
  componentDidLoad() {
    var _a, _b, _c;
    if (this.type === 'number') {
      (_a = this.arrowUpElement) === null || _a === void 0 ? void 0 : _a.addEventListener('mousedown', this.throttleFunction(this.handleIncrementNumber));
      (_b = this.arrowDownElement) === null || _b === void 0 ? void 0 : _b.addEventListener('mousedown', this.throttleFunction(this.handleDecrementNumber));
    }
    this.element.addEventListener('animationstart', this.handleLabelAnimationStart);
    this.element.addEventListener('animationend', this.handleLabelAnimationEnd);
    (_c = this.inputElement) === null || _c === void 0 ? void 0 : _c.addEventListener('animationstart', this.handleAutoFilledByBrowserAnimation);
    this.observeGrowWrapElementResize();
    this.updateClasses();
  }
  /**
   * Before every render:
   * - Update the classes;
   * - Toggle the floating label;
   */
  componentWillRender() {
    const { floatingLabel, value } = this;
    this.updateClasses();
    this.showFloatingLabel = !!(floatingLabel &&
      !this.isFocused &&
      !`${value}`.length &&
      !this.classes.readOnly &&
      !this.isAutoFilledByBrowser);
  }
  /**
   * After every render:
   * - Get the latest element width;
   * - Update the label position;
   * - Update the grow-wrap element content;
   */
  componentDidRender() {
    if (this.processing)
      this.getElementWidth();
    this.calculateLabelPosition();
    this.updateGrowWrapElementContent();
  }
  /**
   * Method to add a tag to the text-field
   */
  async addTag(tag) {
    // Only add the tag to the text-field if the label is not empty or spaces only
    if (tag.label.replace(/\s+/g, '').length) {
      this.tags = [...this.tags, tag];
    }
    this.bindClearInput();
  }
  /**
   * Method to blur the focus on the text-field
   */
  async blurEl() {
    var _a;
    if (!this.disabled) {
      (_a = this.inputElement) === null || _a === void 0 ? void 0 : _a.blur();
    }
  }
  /**
   * Method to clear the value of the text-field
   */
  async clearInput() {
    const emptyValue = '';
    if (this.uncontrolled && this.inputElement) {
      this.value = emptyValue;
      this.inputElement.value = emptyValue;
    }
    emitEvent(this, 'clInput', { component, value: emptyValue }, emptyValue);
  }
  /**
   * Method to focus on the text-field
   */
  async focusEl() {
    var _a;
    if (!this.disabled) {
      (_a = this.inputElement) === null || _a === void 0 ? void 0 : _a.focus();
    }
  }
  /**
   * Method to remove a tag from the text-field
   */
  async removeTag(value) {
    if (!this.disabled && !this.readOnly) {
      if (Object.keys(value || {}).length === 0) {
        this.logger.error.method.removeTag(`Given invalid tag "${value}".`);
        return;
      }
      const tag = value.label || value.detail.id;
      const tagsCopy = [...this.tags];
      const splitValue = tag.split('--');
      const index = splitValue[splitValue.length - 1];
      tagsCopy.splice(index, 1);
      this.tags = tagsCopy;
      const newValue = { tags: this.tags };
      emitEvent(this, 'clInput', Object.assign({ component, value: newValue }, ((value === null || value === void 0 ? void 0 : value.detail) ? { originalEvent: value } : {})), newValue);
      this.focusEl();
    }
  }
  /**
   * Set the necessary functions on inputElementAttributes right before the component mounts to the DOM.
   * This way we prevent unneccesary function assignments after component mount
   */
  componentWillLoad() {
    this.inputElementAttributes = {
      oninput: this.handleOnInput,
      onkeypress: this.handleOnKeyPress,
      onfocus: this.handleOnFocus,
      onblur: this.handleOnBlur,
    };
  }
  render() {
    const { counter, helperText, label, max, maxLength, min, type, value, variant } = this;
    return (h(Host, { class: cl(variant, type, this.classes), style: this.styles }, label && h("div", { class: 'text-field__label-wrapper' }), h("div", { class: 'text-field__input-wrapper' }, h("div", { class: 'text-field__input-wrapper__border' }), h("div", { class: 'text-field__input-wrapper__slot' }, h("slot", { name: 'leading' })), h("div", { class: cl('text-field__input-wrapper__content', {
        'text-field__input-wrapper__content--has-tags': this.hasTagElements,
      }) }, h("cl-typography", Object.assign({ ref: el => {
        this.labelElement = el;
      }, class: cl('text-field__label', {
        'text-field__label__floating': this.showFloatingLabel && !this.isAnimating,
        'text-field__label__fixed': !this.showFloatingLabel && !this.isAnimating,
      }), variant: this.showFloatingLabel ? 'body-1' : 'body-2' }, (this.showFloatingLabel && { onClick: this.focusEl })), label), this.renderInputElement(), type === 'number' && (h("div", { class: 'text-field__input__arrows' }, h("cl-icon", { ref: el => {
        this.arrowUpElement = el;
      }, class: cl('text-field__input__arrows__up', {
        'text-field__input__arrows--limit': isNumber(max) && parseFloat(value) >= max,
      }), name: 'micro--chevron--up', size: 2, onClick: this.handleIncrementNumber, onMouseLeave: this.clearTimers, onMouseUp: this.clearTimers }), h("cl-icon", { ref: el => {
        this.arrowDownElement = el;
      }, class: cl('text-field__input__arrows__down', {
        'text-field__input__arrows--limit': isNumber(min) && parseFloat(value) <= min,
      }), name: 'micro--chevron--down', size: 2, onClick: min === undefined || (isNumber(min) && !(parseFloat(value) <= min))
        ? this.handleDecrementNumber
        : undefined, onMouseLeave: this.clearTimers, onMouseUp: this.clearTimers }))), this.hasTagElements && (h("div", { class: 'text-field__input-wrapper__content--tags-wrapper' }, this.renderTags()))), h("div", { class: 'text-field__input-wrapper__slot' }, h("slot", { name: 'action' }))), (helperText || counter) && (h("div", { class: 'text-field__assistive-content' }, h("div", { class: 'text-field__assistive-content__text' }, helperText), counter && (h("div", { class: 'text-field__assistive-content__counter' }, value.length, isNumber(maxLength) && maxLength > 0 && `/${maxLength}`))))));
  }
  get element() { return this; }
  static get watchers() { return {
    "floatingLabel": ["validateFloatingLabel"],
    "placeholder": ["validatePlaceholder"],
    "required": ["handleRequired"],
    "tags": ["validateTags"],
    "type": ["validateType", "updateGrowWrapElementContent"],
    "variant": ["validateVariant"],
    "fullWidth": ["updateGrowWrapElementContent"],
    "hasTagElements": ["updateGrowWrapElementContent"],
    "multiline": ["updateGrowWrapElementContent"],
    "value": ["updateGrowWrapElementContent"],
    "growWrapElement": ["observeGrowWrapElementResize"],
    "showTextArea": ["observeGrowWrapElementResize"]
  }; }
  static get style() { return clTextFieldCss; }
}, [6, "cl-text-field", {
    "allowSubmit": [4, "allow-submit"],
    "autocomplete": [1],
    "autoSelect": [4, "auto-select"],
    "counter": [4],
    "disabled": [4],
    "error": [4],
    "floatingLabel": [4, "floating-label"],
    "fullWidth": [4, "full-width"],
    "helperText": [1, "helper-text"],
    "inputId": [1, "input-id"],
    "label": [1025],
    "max": [2],
    "maxLength": [2, "maxlength"],
    "min": [2],
    "multiline": [4],
    "multiple": [4],
    "inputName": [1, "input-name"],
    "placeholder": [1],
    "processing": [4],
    "readOnly": [4, "readonly"],
    "required": [4],
    "step": [2],
    "tags": [1040],
    "type": [1],
    "uncontrolled": [516],
    "value": [1025],
    "variant": [1],
    "growWrapElement": [32],
    "inputElement": [32],
    "scrollWrapperElement": [32],
    "styles": [32],
    "classes": [32],
    "inputElementAttributes": [32],
    "hasTagElements": [32],
    "isFocused": [32],
    "isAnimating": [32],
    "isAutoFilledByBrowser": [32],
    "showTextArea": [32],
    "addTag": [64],
    "blurEl": [64],
    "clearInput": [64],
    "focusEl": [64],
    "removeTag": [64]
  }]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-text-field", "cl-chip", "cl-icon", "cl-scroll-wrapper", "cl-typography"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-text-field":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClTextField$1);
      }
      break;
    case "cl-chip":
      if (!customElements.get(tagName)) {
        defineCustomElement$5();
      }
      break;
    case "cl-icon":
      if (!customElements.get(tagName)) {
        defineCustomElement$4();
      }
      break;
    case "cl-scroll-wrapper":
      if (!customElements.get(tagName)) {
        defineCustomElement$3();
      }
      break;
    case "cl-typography":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
  } });
}

const ClTextField = ClTextField$1;
const defineCustomElement = defineCustomElement$1;

export { ClTextField, defineCustomElement };

//# sourceMappingURL=cl-text-field.js.map